import React, { useState, useContext } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/system";
import axios from "axios";
import { AppStateContext } from "../../App";
import baseURL from "../../config/config";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import axiosInstance from "../../helpers/Axios";

const StyledBox = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 30vh;
  padding: 2rem;
  background-color: #f3f4f6;
  margin-top: 5rem;
`;

const FormContainer = styled(Box)`
  background-color: white;
  padding: 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 28rem;
`;

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 0.75rem;
    margin: 1rem;
  }
`;

const DownloadButton = styled(Button)`
  background-color: #2563eb;
  &:hover {
    background-color: #1d4ed8;
  }
`;

const LoaderContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  min-height: 150px;
  gap: 1.5rem;
`;

const Downloadreport = () => {
  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [open, setOpen] = useState(false);
  const { access_token } = useContext(AppStateContext);

  const handleDownloadRequest = async () => {
    alert("this script is disabled")
    return 
    if (!access_token) {
      alert("Unauthorized! Please log in.");
      return;
    }
  
    setLoading(true);
    setOpen(true); // Open dialog immediately to show loading state
  
    try {
      const response = await axiosInstance.post(
        `${baseURL}/api/trail/lufs-report/`,
        {},
      );
  
      // Create a download URL from the response blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      setDownloadUrl(url);
      
      setResponseData({
        status: "success",
        message: "Report generated successfully. Ready for download.",
      });
    } catch (error) {
      console.error("Error generating report:", error);
      setResponseData({
        status: "error",
        message: error.response?.data?.message || error.message || "Failed to generate report! Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (downloadUrl) {
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", `report-${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (downloadUrl) {
      window.URL.revokeObjectURL(downloadUrl);
      setDownloadUrl(null);
    }
    setResponseData(null);
  };

  return (
    <StyledBox>
      <FormContainer>
        <Typography
          variant="h4"
          sx={{ textAlign: "center", mb: 4, fontWeight: 600, color: "#1f2937" }}>
          Download Report
        </Typography>
        
        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, color: "#4b5563" }}>
              Generate Report
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: "#6b7280" }}>
              Click the button below to generate and download the latest report.
              This may take a moment depending on the amount of data.
            </Typography>
          </div>

          <DownloadButton
            variant="contained"
            fullWidth
            sx={{ py: 2, display: "flex", gap: 1 }}
            onClick={handleDownloadRequest}
            disabled={true}
            startIcon={<DescriptionIcon />}>
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Generate & Download Report"
            )}
          </DownloadButton>
        </Box>
      </FormContainer>

      <StyledDialog open={open} onClose={!loading ? handleClose : null} maxWidth="sm" fullWidth>
        <div style={{ position: "relative" }}>
          <div style={{ padding: "16px 24px", borderBottom: "1px solid #e5e7eb" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" sx={{ color: "#1f2937" }}>
                {loading ? "Generating Report" : "Report Status"}
              </Typography>
              {!loading && (
                <IconButton onClick={handleClose} sx={{ color: "#9ca3af", "&:hover": { color: "#4b5563" } }}>
                  <CloseIcon />
                </IconButton>
              )}
            </div>
          </div>

          <DialogContent sx={{ p: 3 }}>
            {loading ? (
              <LoaderContainer>
                <CircularProgress size={60} />
                <Typography variant="h6" color="text.secondary">
                  Generating your report...
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  This may take a few moments. Please don't close this window.
                </Typography>
              </LoaderContainer>
            ) : (
              responseData && (
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "16px",
                      borderRadius: "8px",
                      backgroundColor: responseData.status === "success" ? "#ecfdf5" : "#fef2f2",
                      color: responseData.status === "success" ? "#065f46" : "#b91c1c"
                    }}>
                    {responseData.status === "success" ? (
                      <CheckCircleIcon sx={{ height: "20px", width: "20px", color: "#10b981" }} />
                    ) : (
                      <ErrorIcon sx={{ height: "20px", width: "20px", color: "#ef4444" }} />
                    )}
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>{responseData.message}</Typography>
                  </div>

                  {downloadUrl && (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownload}
                      fullWidth
                      sx={{ mt: 2, py: 1.5 }}>
                      Download Report
                    </Button>
                  )}
                </div>
              )
            )}
          </DialogContent>

          {!loading && (
            <div style={{ borderTop: "1px solid #e5e7eb", padding: "16px 24px", backgroundColor: "#f9fafb" }}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={handleClose} variant="outlined">
                  Close
                </Button>
              </div>
            </div>
          )}
        </div>
      </StyledDialog>
    </StyledBox>
  );
};

export default Downloadreport;
